import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import { PaymentMethod } from "@freightsimple/generated-dashboard-openapi-client/models/PaymentMethod";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { CreditCardsAddDialog } from "@/Features/CreditCards/Exports/Dialogs/CreditCardsAddDialog";
import { CreditCardsShareDialog } from "@/Features/CreditCards/Exports/Dialogs/CreditCardsShareDialog";

export interface UseCreditCardsResult {
  creditCards: Array<CreditCard>;
  bankAccounts: Array<BankAccount>;
  initialLoading: boolean;
  /** Set when the cards failed to load: Try again on the failed-load state. */
  retryFailedLoad: (() => void) | undefined;
  /** Whether the remove action is offered — false when this is the last method. */
  allowRemoveCardButton: boolean;
  showAddCreditCardDialog: () => void;
  showShareDialog: () => void;
  removeCard: (stripePaymentMethodId: string) => void;
  setDefaultCard: (stripePaymentMethodId: string) => void;
  /** The screen renders these (declarative dialogs). */
  dialogs: ReactNode;
}

/**
 * Owns the Credit Cards screen's data + payment-method mutations: loads the
 * saved cards/bank accounts, and wraps remove (with confirm) and set-default
 * (with the expired-card guard, ENG-4368); a failed remove or set-default
 * opens a notice. The screen just renders what this
 * returns, including the `dialogs` node.
 */
export function useCreditCards(): UseCreditCardsResult {
  const [creditCards, setCreditCards] = useState<Array<CreditCard>>([]);
  const [bankAccounts, setBankAccounts] = useState<Array<BankAccount>>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [allowRemoveCardButton, setAllowRemoveCardButton] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string | undefined>();
  const [notice, setNotice] = useState<
    { title: string; message: string } | undefined
  >();

  const paymentMethodsApi = usePaymentMethodsApi();

  async function loadCards() {
    setLoadFailed(false);
    try {
      const response = await paymentMethodsApi.getAllPaymentMethods();
      setCreditCards(response.creditCards);
      setBankAccounts(response.bankAccounts);
      setAllowRemoveCardButton(
        response.creditCards.length + response.bankAccounts.length > 1,
      );
    } catch (e) {
      console.warn("Error loading payment methods:", { e });
      setLoadFailed(true);
    } finally {
      setInitialLoading(false);
    }
  }

  function retryLoad() {
    setInitialLoading(true);
    loadCards();
  }

  useEffect(() => {
    loadCards();
  }, []);

  async function onRemoveConfirmed() {
    if (removeTarget === undefined) return;
    setRemoveTarget(undefined);
    try {
      await paymentMethodsApi.removePaymentMethod({
        removePaymentMethodRequest: {
          stripePaymentMethodId: removeTarget,
        },
      });
    } catch (e) {
      console.warn("Error removing card:", { e });
      setNotice({
        title: "Unable to remove card",
        message: "Something went wrong. Please try again.",
      });
    }
    await loadCards();
  }

  async function setDefaultCard(stripePaymentMethodId: string) {
    try {
      await paymentMethodsApi.setDefaultPaymentMethod({
        setDefaultPaymentMethodRequest: {
          stripePaymentMethodId: stripePaymentMethodId,
          paymentMethod: PaymentMethod.CreditCard,
          newPaymentMethod: false,
        },
      });
    } catch (e) {
      // The menu item is disabled for expired cards, but stale UI state can
      // still reach the backend guard (ENG-4368) — surface it instead of
      // failing silently
      if (e instanceof ResponseError && e.response.status === 409) {
        setNotice({
          title: "Unable to set default payment method",
          message:
            "This card can't be used as the default payment method. It may have expired or been removed. Please refresh and try another card.",
        });
      } else {
        setNotice({
          title: "Unable to set default payment method",
          message: "Something went wrong. Please try again.",
        });
      }
    }
    await loadCards();
  }

  const dialogs: ReactNode = (
    <>
      <CreditCardsAddDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCardAdded={() => loadCards()}
      />
      <CreditCardsShareDialog open={shareOpen} onOpenChange={setShareOpen} />
      <Dialog
        open={removeTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(undefined);
        }}
        title="Confirm"
        description="Are you sure you want to remove this card"
        okText="Remove Card"
        onOk={onRemoveConfirmed}
      >
        <ControlPresentationProvider presentation="underlined">
          <></>
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={notice !== undefined}
        onOpenChange={(open) => {
          if (!open) setNotice(undefined);
        }}
        title={notice?.title}
        footer={
          <Button primary onClick={() => setNotice(undefined)}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {notice?.message}
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  return {
    creditCards,
    bankAccounts,
    initialLoading,
    retryFailedLoad: loadFailed ? retryLoad : undefined,
    allowRemoveCardButton,
    showAddCreditCardDialog: () => setAddOpen(true),
    showShareDialog: () => setShareOpen(true),
    removeCard: (stripePaymentMethodId: string) =>
      setRemoveTarget(stripePaymentMethodId),
    setDefaultCard,
    dialogs,
  };
}
